class Paginatable {
  constructor({ currentPage, perPage, totalItems, pagesToShow = 4 }) {
    this._currentPage = currentPage;
    this.perPage = perPage;
    this.totalItems = totalItems;
    this.pagesToShow = pagesToShow;
    this.ellipsis = "...";
  }

  get isNearFirstPage() {
    return this.currentPage <= 2;
  }

  get isNearLastPage() {
    return this.currentPage >= this.lastPage - 1;
  }

  get isFirstPage() {
    return this.currentPage === 1;
  }

  get isLastPage() {
    return this.currentPage === this.lastPage;
  }

  get prevPage() {
    return this.currentPage - 1;
  }

  get nextPage() {
    return this.currentPage + 1;
  }

  get currentPage() {
    return this._currentPage <= this.lastPage
      ? this._currentPage
      : this.lastPage;
  }

  get lastPage() {
    return Math.ceil(this.totalItems / this.perPage);
  }

  get maxPagesBeforeCurrentPage() {
    return Math.floor(this.pagesToShow / 2);
  }

  get maxPagesAfterCurrentPage() {
    return Math.ceil(this.pagesToShow / 2) - 1;
  }

  get hasOnlyOnePage() {
    return this.lastPage <= 1;
  }

  get isNearCurrentStart() {
    return this.currentPage <= this.maxPagesBeforeCurrentPage;
  }

  get isNearCurrentEnd() {
    return this.currentPage + this.maxPagesAfterCurrentPage >= this.lastPage;
  }

  get startPage() {
    const getValue = () => {
      if (this.isNearCurrentStart) return 1;
      if (this.isNearCurrentEnd) return this.lastPage - this.pagesToShow + 1;
      return this.currentPage - this.maxPagesBeforeCurrentPage;
    };

    return Math.max(1, getValue());
  }

  get endPage() {
    const getValue = () => {
      if (this.isNearCurrentEnd) return this.lastPage;
      if (this.isNearCurrentStart) return this.pagesToShow;
      return this.currentPage + this.maxPagesAfterCurrentPage;
    };

    return Math.min(this.lastPage, getValue());
  }

  get list() {
    if (this.hasOnlyOnePage) return [];
    const startPage = this.startPage;
    const endPage = this.endPage;
    const lastPage = this.lastPage;

    let pages = Array.from(Array(endPage + 1 - startPage).keys())
      .map((page) => startPage + page)
      .filter((page) => page <= lastPage && this.currentPage > 0);

    pages = this.prependFirstPages(pages);
    pages = this.appendLastPages(pages);

    return pages;
  }

  prependFirstPages(pages) {
    const pagesSet = new Set([1, 2, ...pages]);
    if (pages[0] <= 3) return Array.from(pagesSet);

    return [1, this.ellipsis, ...pages];
  }

  appendLastPages(pages) {
    const lastItem = pages[pages.length - 1];
    const lastPage = this.lastPage;
    const pagesSet = new Set([...pages, lastPage - 1, lastPage]);

    if (lastItem >= lastPage - 2) return Array.from(pagesSet);

    return [...pages, this.ellipsis, lastPage];
  }
}

export default Paginatable;
